import {
  Alert,
  Form, FormRow, Option, PopupHint, SegmentedButton, Select
} from '@op/opds.opux';
import { useFormValidationAlert } from '@op/luna.yaweb-common-js/FormValidationAlert';
import { Message, useMessageGetter } from '@messageformat/react';
import { IO_CHANGE_INSURANCE, IO_UPDATE_SEND_VALUE } from '../../constants/actionTypes';
import { DEFAULT_LOCALE } from '../../constants/common';
import { useInsuranceOffer } from '../../hooks';
import { buildElementId } from '../../utils';
import ValidationError from '../common/ValidationError/ValidationError';
import { FormInput } from '../common/Input/FormInput';

const VehiclesQuestions = (insurance) => {
  const { state, validateInsurance, updateSendValue } = useInsuranceOffer();
  const getMessage = useMessageGetter();
  const getDefaultMessage = useMessageGetter([], { locale: DEFAULT_LOCALE });
  const { errors, removeErrors } = useFormValidationAlert();
  const newErrors = { add: [], remove: [] };
  const index = state.selectedInsurances.findIndex(
    (element) => element.id === insurance.id
  );

  const vehicleDetails = state.selectedInsurances[index].questions;
  const vehicles = ['Car', 'Van', 'Lorry'];

  const makeInputData = (data, label, answer, errorLabel) => {
    const value = data || '';
    return {
      value,
      dataKey: `${insurance.id}-${label}`,
      question: getMessage(`labelText.${label}`),
      answer: answer ? getMessage(answer) : value,
      defaultQuestion: getDefaultMessage(`labelText.${label}`),
      defaultAnswer: answer ? getDefaultMessage(answer) : value,
      errorLabel: errorLabel
        ? `${insurance.caption} : ${getMessage(
            `labelText.${label}`
          )} - ${getMessage(errorLabel)}`
        : '',
      getMessage,
    };
  };

  const getValue = (answer) => {
    return (answer || { value: '' }).value;
  };

  const getOptionTitle = (event, key) => {
    const { value, selectedIndex } = event.target;
    return value === 'choose'
      ? 'uiText.choose'
      : `option.${key}.${String(selectedIndex).padStart(3, '0')}`;
  };

  const cleanInputsByIds = (arrayList, errs, insId) => {
    return arrayList
      .map((item) => buildElementId(insId, item)) // Create full IDs
      .filter((fullId) => errs.some((err) => err.id === fullId)); // Filter existing ones
  };

  return (
    <div>
      <Form spacing="default">
        <FormRow
          label={<Message id="labelText.vehicleType" />}
          labelFor={buildElementId(insurance.id, 'VEHICLE_TYPE')}
          dataKey={`${insurance.id}-vehicleType`}
          required
        >
          <Select
            id={buildElementId(insurance.id, 'VEHICLE_TYPE')}
            value={getValue(vehicleDetails.vehicleType)}
            name={`${insurance.id}-vehicleType`}
            aria-describedby='vehicleTypeError'
            onChange={(e) => {
              newErrors.remove = errors
                .filter((record) => record.id.startsWith(insurance.id))
                .map((record) => record.id);
              removeErrors(...newErrors.remove);
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: {
                  vehicleType: makeInputData(
                    e.target.value,
                    'vehicleType',
                    getOptionTitle(e, 'vehicleType'),
                    'error.fill.in.missing.information'
                    
                  ),
                  // miksi? museumVehicleType: vehicleDetails.museumVehicleType,
                },
                questionId: e.target.id,
              });
            }}
          >
            <option value="choose" key="choose">
              {getMessage('uiText.choose')}
            </option>
            <option value="Car" key="Car">
              {getMessage('option.vehicleType.001')}
            </option>
            <option value="Van" key="Van">
              {getMessage('option.vehicleType.002')}
            </option>
            <option value="Motorcycle" key="Motorcycle">
              {getMessage('option.vehicleType.003')}
            </option>
            <option
              value="Moped or light quadricycle"
              key="Moped or light quadricycle"
            >
              {getMessage('option.vehicleType.004')}
            </option>
            <option value="Snowmobile" key="Snowmobile">
              {getMessage('option.vehicleType.005')}
            </option>
            <option value="Trailer" key="Trailer">
              {getMessage('option.vehicleType.006')}
            </option>
            <option value="Caravan" key="Caravan">
              {getMessage('option.vehicleType.007')}
            </option>
            <option value="Campervan" key="Campervan">
              {getMessage('option.vehicleType.008')}
            </option>
            <option value="Lorry" key="Lorry">
              {getMessage('option.vehicleType.009')}
            </option>
            <option value="Tractor" key="Tractor">
              {getMessage('option.vehicleType.010')}
            </option>
            <option value="All-terrain vehicle" key="All-terrain vehicle">
              {getMessage('option.vehicleType.011')}
            </option>
            <option value="Museum vehicle" key="Museum vehicle">
              {getMessage('option.vehicleType.012')}
            </option>
            <option value="Electric Personal Transportation Device" key="Electric Personal Transportation Device">
              {getMessage('option.vehicleType.013')}
            </option>
            <option value="Other vehicle" key="Other vehicle">
              {getMessage('option.vehicleType.014')}
            </option>
          </Select>

          <ValidationError
            id="vehicleTypeError"
            requireVisible={false}
            errorId={buildElementId(insurance.id, 'VEHICLE_TYPE')}
            errorMessageId="error.fill.in.missing.information"
          />
        </FormRow>
      </Form>

      {getValue(vehicleDetails.vehicleType) &&
        getValue(vehicleDetails.vehicleType) !== 'choose' && (
          <div>
            <Form spacing="default">
              {getValue(vehicleDetails.vehicleType) === 'Museum vehicle' && (
                <FormRow
                  label={<Message id="labelText.museumVehicleType" />}
                  labelFor={buildElementId(insurance.id, 'MUSEUM_VEHICLE_TYPE')}
                  dataKey={`${insurance.id}-museumVehicleType`}
                  required
                >
                  <Select
                    id={buildElementId(insurance.id, 'MUSEUM_VEHICLE_TYPE')}
                    value={getValue(vehicleDetails.museumVehicleType)}
                    aria-describedby='museumVehicleTypeError'
                    onChange={(e) => {
                      vehicleDetails.museumVehicleType = makeInputData(
                        e.target.value,
                        'museumVehicleType',
                        getOptionTitle(e, 'museumVehicleType'),
                        'error.fill.in.missing.information'
                      );
                      validateInsurance({
                        insurance,
                        type: IO_CHANGE_INSURANCE,
                        questions: vehicleDetails,
                        questionId: e.target.id,
                      });
                    }}
                  >
                    <option value="choose" key="choose">
                      {getMessage('uiText.choose')}
                    </option>
                    <option value="Car" key="Car">
                      {getMessage('option.museumVehicleType.001')}
                    </option>
                    <option value="Van" key="Van">
                      {getMessage('option.museumVehicleType.002')}
                    </option>
                    <option value="Motorcycle" key="Motorcycle">
                      {getMessage('option.museumVehicleType.003')}
                    </option>
                    <option
                      value="Moped or light quadricycle"
                      key="Moped or light quadricycle"
                    >
                      {getMessage('option.museumVehicleType.004')}
                    </option>
                    <option value="Snowmobile" key="Snowmobile">
                      {getMessage('option.museumVehicleType.005')}
                    </option>
                    <option value="Trailer" key="Trailer">
                      {getMessage('option.museumVehicleType.006')}
                    </option>
                    <option value="Caravan" key="Caravan">
                      {getMessage('option.museumVehicleType.007')}
                    </option>
                    <option value="Campervan" key="Campervan">
                      {getMessage('option.museumVehicleType.008')}
                    </option>
                    <option value="Lorry" key="Lorry">
                      {getMessage('option.museumVehicleType.009')}
                    </option>
                    <option value="Tractor" key="Tractor">
                      {getMessage('option.museumVehicleType.010')}
                    </option>
                    <option
                      value="All-terrain vehicle"
                      key="All-terrain vehicle"
                    >
                      {getMessage('option.museumVehicleType.011')}
                    </option>
                  </Select>

                  <ValidationError
                    id="museumVehicleTypeError"
                    requireVisible={false}
                    errorId={buildElementId(
                      insurance.id,
                      'MUSEUM_VEHICLE_TYPE'
                    )}
                    errorMessageId="error.fill.in.missing.information"
                  />
                </FormRow>
              )}
            {getValue(vehicleDetails.vehicleType) === 'Electric Personal Transportation Device' && (
                <FormRow
                  label={<Message id="labelText.electricPersonalTransportationDeviceVehicleType" />}
                  labelFor={buildElementId(insurance.id, 'ELECTRIC_PERSONAL_TRANSPORTATION_DEVICE_VEHICLE_TYPE')}
                  dataKey={`${insurance.id}-electricPersonalTransportationDeviceVehicleType`}
                  required
                >
                  <Select
                    id={buildElementId(insurance.id, 'ELECTRIC_PERSONAL_TRANSPORTATION_DEVICE_VEHICLE_TYPE')}
                    value={getValue(vehicleDetails.electricPersonalTransportationDeviceVehicleType)}
                    aria-describedby='electricPersonalTransportationDeviceVehicleTypeError'
                    onChange={(e) => {
                      delete vehicleDetails.noElectricMotorAssistanceOver25Kph;
                      delete vehicleDetails.motorPowerOver250W;
                      delete vehicleDetails.topSpeedOver25Kph;
                      delete vehicleDetails.electricPersonalTransportationDeviceAgeUnder24;
                      delete vehicleDetails.electricPersonalTransportationDeviceValue;

                      const clearInputs = ["NO_ELECTRIC_MOTOR_ASSISTANCE_OVER_25_KPH","MOTOR_POWER_OVER_250_W","TOP_SPEED_OVER_25_KPH","ELECTRIC_PERSONAL_TRANSPORTATION_DEVICE_AGE_UNDER_24", "ELECTRIC_PERSONAL_TRANSPORTATION_DEVICE_VALUE"]
                      newErrors.remove = [...newErrors.remove, ...cleanInputsByIds(clearInputs, errors, insurance.id)]
                      removeErrors(...newErrors.remove)

                      vehicleDetails.electricPersonalTransportationDeviceVehicleType = makeInputData(
                        e.target.value,
                        'electricPersonalTransportationDeviceVehicleType',
                        getOptionTitle(e, 'electricPersonalTransportationDeviceVehicleType'),
                        'error.fill.in.missing.information'
                      );
                      validateInsurance({
                        insurance,
                        type: IO_CHANGE_INSURANCE,
                        questions: vehicleDetails,
                        questionId: e.target.id,
                      });
                    }}
                  >
                    <option value="choose" key="choose">
                      {getMessage('uiText.choose')}
                    </option>
                    <option value="Electric bicycle" key="Electric bicycle">
                      {getMessage('option.electricPersonalTransportationDeviceVehicleType.001')}
                    </option>
                    <option value="Light electric vehicle" key="Light electric vehicle">
                      {getMessage('option.electricPersonalTransportationDeviceVehicleType.002')}
                    </option>
                  </Select>

                  <ValidationError
                    id="electricPersonalTransportationDeviceVehicleTypeError"
                    requireVisible={false}
                    errorId={buildElementId(
                      insurance.id,
                      'ELECTRIC_PERSONAL_TRANSPORTATION_DEVICE_VEHICLE_TYPE'
                    )}
                    errorMessageId="error.fill.in.missing.information"
                  />
                </FormRow>
              )}
            {getValue(vehicleDetails.electricPersonalTransportationDeviceVehicleType) === 'Electric bicycle' && (
              <FormRow
                label={<Message id="labelText.noElectricMotorAssistanceOver25Kph" />}
                labelFor={buildElementId(insurance.id, 'NO_ELECTRIC_MOTOR_ASSISTANCE_OVER_25_KPH')}
                dataKey={`${insurance.id}-noElectricMotorAssistanceOver25Kph`}
                required
              >
                <SegmentedButton
                  id={buildElementId(insurance.id, 'NO_ELECTRIC_MOTOR_ASSISTANCE_OVER_25_KPH')}
                  aria-describedby='noElectricMotorAssistanceOver25KphError'
                  value={getValue(vehicleDetails.noElectricMotorAssistanceOver25Kph)}
                  name={`${insurance.id}-noElectricMotorAssistanceOver25Kph`}
                  onChange={(e) => {
                    vehicleDetails.noElectricMotorAssistanceOver25Kph = makeInputData(
                      e.target.value,
                      'noElectricMotorAssistanceOver25Kph',
                      `option.${e.target.value.toLowerCase()}`
                    );
                    validateInsurance({
                      insurance,
                      type: IO_CHANGE_INSURANCE,
                      questions: vehicleDetails,
                      questionId: buildElementId(
                        insurance.id,
                        'NO_ELECTRIC_MOTOR_ASSISTANCE_OVER_25_KPH'
                      ),
                    });
                    updateSendValue({
                      type: IO_UPDATE_SEND_VALUE,
                      id: insurance.id,
                      value: showExtraQuestions(vehicleDetails),
                    });
                  }}
                >
                  <Option value="Yes" key="Yes">
                    {<Message id="option.yes" />}
                  </Option>
                  <Option value="No" key="No">
                    {<Message id="option.no" />}
                  </Option>
                </SegmentedButton>

                <ValidationError
                  id="noElectricMotorAssistanceOver25KphError"
                  requireVisible={false}
                  errorId={buildElementId(insurance.id, 'NO_ELECTRIC_MOTOR_ASSISTANCE_OVER_25_KPH')}
                  errorMessageId="error.fill.in.missing.information"
                />
              </FormRow>
            )}
            {getValue(vehicleDetails.electricPersonalTransportationDeviceVehicleType) === 'Electric bicycle' && (
              <FormRow
                label={<Message id="labelText.motorPowerOver250W" />}
                labelFor={buildElementId(insurance.id, 'MOTOR_POWER_OVER_250_W')}
                dataKey={`${insurance.id}-motorPowerOver250W`}
              >
                <SegmentedButton
                  id={buildElementId(insurance.id, 'MOTOR_POWER_OVER_250_W')}
                  aria-describedby='motorPowerOver250WError'
                  value={getValue(vehicleDetails.motorPowerOver250W)}
                  name={`${insurance.id}-motorPowerOver250W`}
                  onChange={(e) => {
                    vehicleDetails.motorPowerOver250W = makeInputData(
                      e.target.value,
                      'motorPowerOver250W',
                      `option.${e.target.value.toLowerCase()}`
                    );
                    validateInsurance({
                      insurance,
                      type: IO_CHANGE_INSURANCE,
                      questions: vehicleDetails,
                      questionId: buildElementId(
                        insurance.id,
                        'MOTOR_POWER_OVER_250_W'
                      ),
                    });
                    updateSendValue({
                      type: IO_UPDATE_SEND_VALUE,
                      id: insurance.id,
                      value: showExtraQuestions(vehicleDetails),
                    });
                  }}
                >
                  <Option value="Yes" key="Yes" required>
                    {<Message id="option.yes" />}
                  </Option>
                  <Option value="No" key="No">
                    {<Message id="option.no" />}
                  </Option>
                </SegmentedButton>

                <ValidationError
                  id="motorPowerOver250WError"
                  requireVisible={false}
                  errorId={buildElementId(insurance.id, 'MOTOR_POWER_OVER_250_W')}
                  errorMessageId="error.fill.in.missing.information"
                />
              </FormRow>
            )}
            {getValue(vehicleDetails.electricPersonalTransportationDeviceVehicleType) === 'Light electric vehicle' && (
              <FormRow
                label={<Message id="labelText.topSpeedOver25Kph" />}
                labelFor={buildElementId(insurance.id, 'TOP_SPEED_OVER_25_KPH')}
                dataKey={`${insurance.id}-topSpeedOver25Kph`}
              >
                <SegmentedButton
                  id={buildElementId(insurance.id, 'TOP_SPEED_OVER_25_KPH')}
                  aria-describedby='topSpeedOver25KphError'
                  value={getValue(vehicleDetails.topSpeedOver25Kph)}
                  name={`${insurance.id}-topSpeedOver25Kph`}
                  onChange={(e) => {
                    vehicleDetails.topSpeedOver25Kph = makeInputData(
                      e.target.value,
                      'topSpeedOver25Kph',
                      `option.${e.target.value.toLowerCase()}`
                    );
                    validateInsurance({
                      insurance,
                      type: IO_CHANGE_INSURANCE,
                      questions: vehicleDetails,
                      questionId: buildElementId(
                        insurance.id,
                        'TOP_SPEED_OVER_25_KPH'
                      ),
                    });
                    updateSendValue({
                      type: IO_UPDATE_SEND_VALUE,
                      id: insurance.id,
                      value: showExtraQuestions(vehicleDetails),
                    });
                  }}
                >
                  <Option value="Yes" key="Yes" required>
                    {<Message id="option.yes" />}
                  </Option>
                  <Option value="No" key="No">
                    {<Message id="option.no" />}
                  </Option>
                </SegmentedButton>

                <ValidationError
                  id="topSpeedOver25KphError"
                  requireVisible={false}
                  errorId={buildElementId(insurance.id, 'TOP_SPEED_OVER_25_KPH')}
                  errorMessageId="error.fill.in.missing.information"
                />
              </FormRow>
            )}
            {(getValue(vehicleDetails.electricPersonalTransportationDeviceVehicleType) === 'Electric bicycle'  || getValue(vehicleDetails.electricPersonalTransportationDeviceVehicleType) === 'Light electric vehicle') && (
              <FormRow
                label={<Message id={"labelText." + getWeighMoreLabel(getValue(vehicleDetails.electricPersonalTransportationDeviceVehicleType))} />}
                labelFor={buildElementId(insurance.id, 'WEIGHT MORE THAN 25KG')}
                dataKey={`${insurance.id}-weighMoreThan25Kg`}
              >
                <SegmentedButton
                  id={buildElementId(insurance.id, 'WEIGHT MORE THAN 25KG')}
                  aria-describedby='weighMoreThan25KgError'
                  value={getValue(vehicleDetails.weighMoreThan25Kg)}
                  name={`${insurance.id}-weighMoreThan25Kg`}
                  onChange={(e) => {
                    vehicleDetails.weighMoreThan25Kg = makeInputData(
                      e.target.value,
                      getWeighMoreLabel(getValue(vehicleDetails.electricPersonalTransportationDeviceVehicleType)),
                      `option.${e.target.value.toLowerCase()}`
                    );
                    validateInsurance({
                      insurance,
                      type: IO_CHANGE_INSURANCE,
                      questions: vehicleDetails,
                      questionId: buildElementId(
                        insurance.id,
                        'WEIGHT MORE THAN 25KG'
                      ),
                    });
                    updateSendValue({
                      type: IO_UPDATE_SEND_VALUE,
                      id: insurance.id,
                      value: showExtraQuestions(vehicleDetails),
                    });
                  }}
                >
                  <Option value="Yes" key="Yes" required>
                    {<Message id="option.yes" />}
                  </Option>
                  <Option value="No" key="No">
                    {<Message id="option.no" />}
                  </Option>
                </SegmentedButton>

                <ValidationError
                  id="weighMoreThan25KgError"
                  requireVisible={false}
                  errorId={buildElementId(insurance.id, 'WEIGHT MORE THAN 25KG')}
                  errorMessageId="error.fill.in.missing.information"
                />
              </FormRow>
            )}
            
            {showElectricBicycleAlert(getValue(vehicleDetails.noElectricMotorAssistanceOver25Kph), getValue(vehicleDetails.motorPowerOver250W), getValue(vehicleDetails.weighMoreThan25Kg)) && getValue(vehicleDetails.electricPersonalTransportationDeviceVehicleType) === 'Electric bicycle' && <Alert type="positive" aria-live="polite" role="status"><Message id="uiText.doesnNotRequireMotorLiabilityInsuranceElectricBike" /></Alert>}           
            {showLightElectricVehicleAlert(getValue(vehicleDetails.topSpeedOver25Kph), getValue(vehicleDetails.weighMoreThan25Kg)) && getValue(vehicleDetails.electricPersonalTransportationDeviceVehicleType) === 'Light electric vehicle' && <Alert type="positive" aria-live="polite" role="status"><Message id="uiText.doesnNotRequireMotorLiabilityInsuranceElectricVehicle" /></Alert>}
            
            {(
                (
                  showElectricBicycleExtraQuestions(
                    getValue(vehicleDetails.noElectricMotorAssistanceOver25Kph),
                    getValue(vehicleDetails.motorPowerOver250W),
                    getValue(vehicleDetails.weighMoreThan25Kg)
                  ) && 
                  getValue(vehicleDetails.electricPersonalTransportationDeviceVehicleType) === 'Electric bicycle'
                ) || 
                (
                  showLightElectricVehicleExtraQuestions(
                    getValue(vehicleDetails.topSpeedOver25Kph),
                    getValue(vehicleDetails.weighMoreThan25Kg)
                  ) && 
                  getValue(vehicleDetails.electricPersonalTransportationDeviceVehicleType) === 'Light electric vehicle'
                )
              ) && ( 
              <>
                <FormInput
                  type="stringwithoutSP"
                  maxLength={100}
                  value={getValue(vehicleDetails.electricPersonalTransportationDeviceMake)}
                  inputId={buildElementId(insurance.id,'ELECTRIC_PERSONAL_TRANSPORTATION_DEVICE_MAKE')}
                  onChange={(electricPersonalTransportationDeviceMake , error) => {
                    vehicleDetails.electricPersonalTransportationDeviceMake = makeInputData(
                      electricPersonalTransportationDeviceMake,
                      'electricPersonalTransportationDeviceMake'
                    );
                    validateInsurance({
                      insurance,
                      type: IO_CHANGE_INSURANCE,
                      questions: vehicleDetails,
                      questionId: `${insurance.id}-electricPersonalTransportationDeviceMake`,
                      error,
                    });                
                  }}
                  label={getMessage('labelText.electricPersonalTransportationDeviceMake')}
                  required
                />

              <FormInput
                type="stringwithoutSP"
                maxLength={100}
                value={getValue(vehicleDetails.electricPersonalTransportationDeviceModel)}
                inputId={buildElementId(insurance.id,'ELECTRIC_PERSONAL_TRANSPORTATION_DEVICE_MODEL')}
                onChange={(electricPersonalTransportationDeviceModel , error) => {
                  vehicleDetails.electricPersonalTransportationDeviceModel = makeInputData(
                    electricPersonalTransportationDeviceModel,
                    'electricPersonalTransportationDeviceModel'
                  );
                  validateInsurance({
                    insurance,
                    type: IO_CHANGE_INSURANCE,
                    questions: vehicleDetails,
                    questionId: `${insurance.id}-electricPersonalTransportationDeviceModel`,
                    error,
                  });                
                }}
                label={getMessage('labelText.electricPersonalTransportationDeviceModel')}
                required
              /> 

              <FormInput
                type="year"
                maxLength={4}
                limit={1900}
                value={getValue(vehicleDetails.electricPersonalTransportationDeviceFirstYearOfService)}
                inputId={buildElementId(insurance.id, 'ELECTRIC_PERSONAL_TRANSPORTATION_DEVICE_FIRST_YEAR_OF_SERVICE')}
                onChange={(electricPersonalTransportationDeviceFirstYearOfService , error) => {
                  vehicleDetails.electricPersonalTransportationDeviceFirstYearOfService = makeInputData(
                    electricPersonalTransportationDeviceFirstYearOfService,
                    'electricPersonalTransportationDeviceFirstYearOfService'
                  );
                  validateInsurance({
                    insurance,
                    type: IO_CHANGE_INSURANCE,
                    questions: vehicleDetails,
                    questionId: `${insurance.id}-electricPersonalTransportationDeviceFirstYearOfService`,
                    error,
                  });                
                }}
                label={getMessage('labelText.electricPersonalTransportationDeviceFirstYearOfService')}
                required
              /> 

              <FormInput
                type="stringwithoutSP"
                maxLength={100}
                value={getValue(vehicleDetails.electricPersonalTransportationDeviceSerialNumber)}
                inputId={buildElementId(insurance.id,'ELECTRIC_PERSONAL_TRANSPORTATION_DEVICE_SERIAL_NUMBER')}
                onChange={(electricPersonalTransportationDeviceSerialNumber , error) => {
                  vehicleDetails.electricPersonalTransportationDeviceSerialNumber = makeInputData(
                    electricPersonalTransportationDeviceSerialNumber,
                    'electricPersonalTransportationDeviceSerialNumber'
                  );
                  validateInsurance({
                    insurance,
                    type: IO_CHANGE_INSURANCE,
                    questions: vehicleDetails,
                    questionId: `${insurance.id}-electricPersonalTransportationDeviceSerialNumber`,
                    error,
                  });                
                }}
                hint={
                  <PopupHint placement="top">
                    <Message id="hintText.electricPersonalTransportationDeviceSerialNumber" />
                  </PopupHint>
                }
                label={getMessage('labelText.electricPersonalTransportationDeviceSerialNumber')}
                required
              />   

              <FormInput
                type="numeric"
                maxLength={4}
                value={getValue(vehicleDetails.electricPersonalTransportationDevicEnginePower)}
                inputId={buildElementId(insurance.id,'ELECTRIC_PERSONAL_TRANSPORTATION_DEVICE_ENGINE_POWER')}
                onChange={(electricPersonalTransportationDevicEnginePower , error) => {
                  vehicleDetails.electricPersonalTransportationDevicEnginePower = makeInputData(
                    electricPersonalTransportationDevicEnginePower,
                    'electricPersonalTransportationDevicEnginePower'
                  );
                  validateInsurance({
                    insurance,
                    type: IO_CHANGE_INSURANCE,
                    questions: vehicleDetails,
                    questionId: `${insurance.id}-electricPersonalTransportationDevicEnginePower`,
                    error,
                  });                
                }}
                label={getMessage('labelText.electricPersonalTransportationDevicEnginePower')}
                required
              /> 

              <FormInput
                type="numeric"
                maxLength={3}
                value={getValue(vehicleDetails.electricPersonalTransportationDeviceMaximumDesignSpeed)}
                inputId={buildElementId(insurance.id,'ELECTRIC_PERSONAL_TRANSPORTATION_DEVICE_MAXIMUM_DESIGN_SPEED')}
                onChange={(electricPersonalTransportationDeviceMaximumDesignSpeed , error) => {
                  vehicleDetails.electricPersonalTransportationDeviceMaximumDesignSpeed = makeInputData(
                    electricPersonalTransportationDeviceMaximumDesignSpeed,
                    'electricPersonalTransportationDeviceMaximumDesignSpeed'
                  );
                  validateInsurance({
                    insurance,
                    type: IO_CHANGE_INSURANCE,
                    questions: vehicleDetails,
                    questionId: `${insurance.id}-electricPersonalTransportationDeviceMaximumDesignSpeed`,
                    error,
                  });                
                }}
                label={getMessage('labelText.electricPersonalTransportationDeviceMaximumDesignSpeed')}
                required
              />

              <FormInput
                type="numeric"
                maxLength={3}
                value={getValue(vehicleDetails.electricPersonalTransportationDeviceTotalWeight)}
                inputId={buildElementId(insurance.id,'ELECTRIC_PERSONAL_TRANSPORTATION_DEVICE_TOTAL_WEIGHT')}
                onChange={(electricPersonalTransportationDeviceTotalWeight , error) => {
                  vehicleDetails.electricPersonalTransportationDeviceTotalWeight = makeInputData(
                    electricPersonalTransportationDeviceTotalWeight,
                    'electricPersonalTransportationDeviceTotalWeight'
                  );
                  validateInsurance({
                    insurance,
                    type: IO_CHANGE_INSURANCE,
                    questions: vehicleDetails,
                    questionId: `${insurance.id}-electricPersonalTransportationDeviceTotalWeight`,
                    error,
                  });                
                }}
                label={getMessage('labelText.electricPersonalTransportationDeviceTotalWeight')}
                required
                hint={
                  <PopupHint placement="top">
                    <Message id="hintText.electricPersonalTransportationDeviceTotalWeight" />
                  </PopupHint>
                }
              /> 
              

              </>
                         
            )}

            {showElectricBicycleExtraQuestions(
                    getValue(vehicleDetails.noElectricMotorAssistanceOver25Kph),
                    getValue(vehicleDetails.motorPowerOver250W),
                    getValue(vehicleDetails.weighMoreThan25Kg)
                  ) && getValue(vehicleDetails.electricPersonalTransportationDeviceVehicleType) === 'Electric bicycle' && (
              <FormInput
                type="numeric"
                maxLength={6}
                value={getValue(vehicleDetails.electricPersonalTransportationDeviceValue)}
                inputId={buildElementId(insurance.id,'ELECTRIC_PERSONAL_TRANSPORTATION_DEVICE_VALUE')}
                onChange={(electricPersonalTransportationDeviceValue , error) => {
                  vehicleDetails.electricPersonalTransportationDeviceValue = makeInputData(
                    electricPersonalTransportationDeviceValue,
                    'electricPersonalTransportationDeviceValue'
                  );
                  validateInsurance({
                    insurance,
                    type: IO_CHANGE_INSURANCE,
                    questions: vehicleDetails,
                    questionId: `${insurance.id}-electricPersonalTransportationDeviceValue`,
                    error,
                  });                
                }}
                label={getMessage('labelText.electricPersonalTransportationDeviceValue')}
                required
                hint={
                  <PopupHint placement="top">
                    <Message id="hintText.electricPersonalTransportationDeviceValue" />
                  </PopupHint>
                }
              />
            )}

            {showLightElectricVehicleExtraQuestions(
                    getValue(vehicleDetails.topSpeedOver25Kph),
                    getValue(vehicleDetails.weighMoreThan25Kg)
                  ) && getValue(vehicleDetails.electricPersonalTransportationDeviceVehicleType) === 'Light electric vehicle' && (
              <FormRow
              label={<Message id="labelText.electricPersonalTransportationDeviceAgeUnder24" />}
              labelFor={buildElementId(insurance.id, 'ELECTRIC_PERSONAL_TRANSPORTATION_DEVICE_AGE_UNDER_24')}
              dataKey={`${insurance.id}-electricPersonalTransportationDeviceAgeUnder24`}
            >
              <SegmentedButton
                id={buildElementId(insurance.id, 'ELECTRIC_PERSONAL_TRANSPORTATION_DEVICE_AGE_UNDER_24')}
                aria-describedby='electricPersonalTransportationDeviceAgeUnder24Error'
                value={getValue(vehicleDetails.electricPersonalTransportationDeviceAgeUnder24)}
                name={`${insurance.id}-electricPersonalTransportationDeviceAgeUnder24`}
                onChange={(e) => {
                  vehicleDetails.electricPersonalTransportationDeviceAgeUnder24 = makeInputData(
                    e.target.value,
                    'electricPersonalTransportationDeviceAgeUnder24',
                    `option.${e.target.value.toLowerCase()}`
                  );
                  validateInsurance({
                    insurance,
                    type: IO_CHANGE_INSURANCE,
                    questions: vehicleDetails,
                    questionId: buildElementId(
                      insurance.id,
                      'ELECTRIC_PERSONAL_TRANSPORTATION_DEVICE_AGE_UNDER_24'
                    ),
                  });
                }}
              >
                <Option value="Yes" key="Yes" required>
                  {<Message id="option.yes" />}
                </Option>
                <Option value="No" key="No">
                  {<Message id="option.no" />}
                </Option>
              </SegmentedButton>

              <ValidationError
                id="electricPersonalTransportationDeviceAgeUnder24Error"
                requireVisible={false}
                errorId={buildElementId(insurance.id, 'ELECTRIC_PERSONAL_TRANSPORTATION_DEVICE_AGE_UNDER_24')}
                errorMessageId="error.fill.in.missing.information"
              />
            </FormRow>
            )}

            {(
                (
                  showElectricBicycleExtraQuestions(
                    getValue(vehicleDetails.noElectricMotorAssistanceOver25Kph),
                    getValue(vehicleDetails.motorPowerOver250W),
                    getValue(vehicleDetails.weighMoreThan25Kg)
                  ) && 
                  getValue(vehicleDetails.electricPersonalTransportationDeviceVehicleType) === 'Electric bicycle'
                ) || 
                (
                  showLightElectricVehicleExtraQuestions(
                    getValue(vehicleDetails.topSpeedOver25Kph),
                    getValue(vehicleDetails.weighMoreThan25Kg)
                  ) && 
                  getValue(vehicleDetails.electricPersonalTransportationDeviceVehicleType) === 'Light electric vehicle'
                )
              ) &&  (
              <FormRow
                label={<Message id="labelText.electricPersonalTransportationDeviceCollisionCrashCoverage" />}
                labelFor={buildElementId(insurance.id, 'ELECTRIC_PERSONAL_TRANSPORTATION_DEVICE_COLLISION_CRASH_COVERAGE')}
                dataKey={`${insurance.id}-electricPersonalTransportationDeviceCollisionCrashCoverage`}
              >
                <SegmentedButton
                  id={buildElementId(insurance.id, 'ELECTRIC_PERSONAL_TRANSPORTATION_DEVICE_COLLISION_CRASH_COVERAGE')}
                  aria-describedby='electricPersonalTransportationDeviceCollisionCrashCoverageError'
                  value={getValue(vehicleDetails.electricPersonalTransportationDeviceCollisionCrashCoverage)}
                  name={`${insurance.id}-electricPersonalTransportationDeviceCollisionCrashCoverage`}
                  onChange={(e) => {
                    vehicleDetails.electricPersonalTransportationDeviceCollisionCrashCoverage = makeInputData(
                      e.target.value,
                      'electricPersonalTransportationDeviceCollisionCrashCoverage',
                      `option.${e.target.value.toLowerCase()}`
                    );
                    validateInsurance({
                      insurance,
                      type: IO_CHANGE_INSURANCE,
                      questions: vehicleDetails,
                      questionId: buildElementId(
                        insurance.id,
                        'ELECTRIC_PERSONAL_TRANSPORTATION_DEVICE_COLLISION_CRASH_COVERAGE'
                      ),
                    });
                  }}
                >
                  <Option value="Yes" key="Yes" required>
                    {<Message id="option.yes" />}
                  </Option>
                  <Option value="No" key="No">
                    {<Message id="option.no" />}
                  </Option>
                </SegmentedButton>

                <ValidationError
                  id="electricPersonalTransportationDeviceCollisionCrashCoverageError"
                  requireVisible={false}
                  errorId={buildElementId(insurance.id, 'ELECTRIC_PERSONAL_TRANSPORTATION_DEVICE_COLLISION_CRASH_COVERAGE')}
                  errorMessageId="error.fill.in.missing.information"
                />
              </FormRow>
            )}

            {(getValue(vehicleDetails.vehicleType) !== 'Other vehicle' && getValue(vehicleDetails.vehicleType) !== 'Electric Personal Transportation Device') && (
              <FormInput
                type="stringwithoutSP"
                maxLength={20}
                value={getValue(vehicleDetails.regno)}
                inputId={buildElementId(insurance.id,'REGNO')}
                onChange={(regno , error) => {
                  vehicleDetails.regno = makeInputData(
                    regno,
                    'RegistrationNumber'
                  );
                  validateInsurance({
                    insurance,
                    type: IO_CHANGE_INSURANCE,
                    questions: vehicleDetails,
                    questionId: `${insurance.id}-RegistrationNumber`,
                    error,
                  });                
                }}
                label={getMessage('labelText.RegistrationNumber')}
                limit={3}
                required
              />             
            )}

            {(getValue(vehicleDetails.vehicleType) === 'Tractor') && (
              <FormRow
                label={<Message id="labelText.tractorInPrivateUse" />}
                labelFor={buildElementId(insurance.id, 'TRACTOR_IN_PRIVATE_USE')}
                dataKey={`${insurance.id}-tractorInPrivateUse`}
                hint={
                  <PopupHint placement="top">
                    <Message id="hintText.tractorInPrivateUse" />
                  </PopupHint>
                }
              >
                <SegmentedButton
                  id={buildElementId(insurance.id, 'TRACTOR_IN_PRIVATE_USE')}
                  aria-describedby='tractorInPrivateUseError'
                  value={getValue(vehicleDetails.tractorInPrivateUse)}
                  name={`${insurance.id}-tractorInPrivateUse`}
                  onChange={(e) => {
                    vehicleDetails.tractorInPrivateUse = makeInputData(
                      e.target.value,
                      'tractorInPrivateUse',
                      `option.${e.target.value.toLowerCase()}`
                    );
                    validateInsurance({
                      insurance,
                      type: IO_CHANGE_INSURANCE,
                      questions: vehicleDetails,
                      questionId: buildElementId(
                        insurance.id,
                        'TRACTOR_IN_PRIVATE_USE'
                      ),
                    });
                  }}
                >
                  <Option value="Yes" key="Yes" required>
                    {<Message id="option.yes" />}
                  </Option>
                  <Option value="No" key="No">
                    {<Message id="option.no" />}
                  </Option>
                </SegmentedButton>

                <ValidationError
                  id="tractorInPrivateUseError"
                  requireVisible={false}
                  errorId={buildElementId(insurance.id, 'TRACTOR_IN_PRIVATE_USE')}
                  errorMessageId="error.fill.in.missing.information"
                />
              </FormRow>
            )}

            {getValue(vehicleDetails.vehicleType) === 'Other vehicle' && (
              <FormInput
                type="stringwithoutSP"
                maxLength={20}
                value={getValue(vehicleDetails.muuajoneuvoregno)}
                inputId={buildElementId(insurance.id,'MUUAJONEUVOREGNO')}
                onChange={(muuajoneuvoregno , error) => {
                  vehicleDetails.muuajoneuvoregno = makeInputData(
                    muuajoneuvoregno,
                    'MuuAjoneuvoRegistrationNumber'
                  );
                  validateInsurance({
                    insurance,
                    type: IO_CHANGE_INSURANCE,
                    questions: vehicleDetails,
                    questionId: `${insurance.id}-MuuAjoneuvoRegistrationNumber`,
                    error,
                  });                
                }}
                label={getMessage('labelText.MuuAjoneuvoRegistrationNumber')}
                limit={3}
                required
              />              
            )}

              {(vehicles.indexOf(getValue(vehicleDetails.vehicleType)) > -1 ||
                (getValue(vehicleDetails.vehicleType) === 'Museum vehicle' &&
                  vehicles.indexOf(getValue(vehicleDetails.museumVehicleType)) >
                    -1)) && (
                  <FormInput
                    type="numeric"
                    limit={999999}
                    value={getValue(vehicleDetails.vehicleValue)}
                    amount
                    unit={<Message id="unitText.euros" />}
                    unitPos="outside"
                    inputId={buildElementId(insurance.id,'VEHICLE_VALUE')}
                    onChange={(vehicleValue , error) => {
                      vehicleDetails.vehicleValue = makeInputData(
                        vehicleValue,
                        'vehicleValue'
                      );
                      validateInsurance({
                        insurance,
                        type: IO_CHANGE_INSURANCE,
                        questions: vehicleDetails,
                        questionId: `${insurance.id}-vehicleValue`,
                        error,
                      });                
                    }}
                    label={getMessage('labelText.vehicleValue')}
                    hint={
                      <PopupHint placement="top">
                        <Message id="hintText.vehicleValue" />
                      </PopupHint>
                    }
                    required
                  />               
              )}

              {getValue(vehicleDetails.vehicleType) !== 'Electric Personal Transportation Device' && (
              <FormRow
                label={<Message id="labelText.DamageCoverage" />}
                labelFor={buildElementId(insurance.id, 'DAMAGE_COVERAGE')}
                dataKey={`${insurance.id}-DamageCoverage`}
                hint={
                  <PopupHint placement="top">
                    <Message id="hintText.DamageCoverage" />
                  </PopupHint>
                }
              >
                <SegmentedButton
                  id={buildElementId(insurance.id, 'DAMAGE_COVERAGE')}
                  aria-describedby='damageCoverageError'
                  value={getValue(vehicleDetails.damageCoverage)}
                  name={`${insurance.id}-damageCoverage`}
                  onChange={(e) => {
                    vehicleDetails.damageCoverage = makeInputData(
                      e.target.value,
                      'DamageCoverage',
                      `option.${e.target.value.toLowerCase()}`
                    );
                    validateInsurance({
                      insurance,
                      type: IO_CHANGE_INSURANCE,
                      questions: vehicleDetails,
                      questionId: buildElementId(
                        insurance.id,
                        'DAMAGE_COVERAGE'
                      ),
                    });
                  }}
                >
                  <Option value="Yes" key="Yes" required>
                    {<Message id="option.yes" />}
                  </Option>
                  <Option value="No" key="No">
                    {<Message id="option.no" />}
                  </Option>
                </SegmentedButton>

                <ValidationError
                  id="damageCoverageError"
                  requireVisible={false}
                  errorId={buildElementId(insurance.id, 'DAMAGE_COVERAGE')}
                  errorMessageId="error.fill.in.missing.information"
                />
              </FormRow>
              )}
              {getValue(vehicleDetails.vehicleType) !== 'Electric Personal Transportation Device' && (
              <FormRow
                label={<Message id="labelText.vvhConsent" />}
                labelFor={buildElementId(insurance.id, 'VVH_CONSENT')}
                dataKey={`${insurance.id}-vvhConsent`}
                hint={
                  <PopupHint placement="top">
                    <Message id="hintText.vvhConsent" />
                  </PopupHint>
                }
              >
                <SegmentedButton
                  id={buildElementId(insurance.id, 'VVH_CONSENT')}
                  aria-describedby='vvhConsentError'
                  value={getValue(vehicleDetails.vvhConsent)}
                  name={`${insurance.id}-vvhConsent`}
                  onChange={(e) => {
                    vehicleDetails.vvhConsent = makeInputData(
                      e.target.value,
                      'vvhConsent',
                      `option.${e.target.value.toLowerCase()}`
                    );
                    validateInsurance({
                      insurance,
                      type: IO_CHANGE_INSURANCE,
                      questions: vehicleDetails,
                      questionId: buildElementId(insurance.id, 'VVH_CONSENT'),
                    });
                  }}
                >
                  <Option value="Yes" key="Yes" required>
                    {<Message id="option.yes" />}
                  </Option>
                  <Option value="No" key="No">
                    {<Message id="option.no" />}
                  </Option>
                </SegmentedButton>

                <ValidationError
                  id="vvhConsentError"
                  requireVisible={false}
                  errorId={buildElementId(insurance.id, 'VVH_CONSENT')}
                  errorMessageId="error.fill.in.missing.information"
                />
              </FormRow>
              )}
              {(getValue(vehicleDetails.vehicleType) === 'Car' ||
                (getValue(vehicleDetails.vehicleType) === 'Museum vehicle' &&
                  getValue(vehicleDetails.museumVehicleType) === 'Car')) && (
                <FormRow
                  label={<Message id="labelText.vehicleMileage" />}
                  labelFor={buildElementId(insurance.id, 'VEHICLE_MILEAGE')}
                  dataKey={`${insurance.id}-vehicleMileage`}
                  hint={
                    <PopupHint placement="top">
                      <Message id="hintText.vehicleMileage" />
                    </PopupHint>
                  }
                  required
                >
                  <Select
                    id={buildElementId(insurance.id, 'VEHICLE_MILEAGE')}
                    aria-describedby='vehicleMileageError'
                    value={getValue(vehicleDetails.vehicleMileage)}
                    onChange={(e) => {
                      vehicleDetails.vehicleMileage = makeInputData(
                        e.target.value,
                        'vehicleMileage',
                        getOptionTitle(e, 'vehicleMileage'),
                        'error.fill.in.missing.information'                      
                      );
                      validateInsurance({
                        insurance,
                        type: IO_CHANGE_INSURANCE,
                        questions: vehicleDetails,
                        questionId: e.target.id,
                      });
                    }}
                  >
                    <option value="choose" key="choose">
                      {getMessage('uiText.choose')}
                    </option>
                    <option value="Upto 5000km" key="Upto 5000km">
                      {getMessage('option.vehicleMileage.001')}
                    </option>
                    <option value="Upto 10000km" key="Upto 10000km">
                      {getMessage('option.vehicleMileage.002')}
                    </option>
                    <option value="Upto 15000km" key="Upto 15000km">
                      {getMessage('option.vehicleMileage.003')}
                    </option>
                    <option value="Upto 20000km" key="Upto 20000km">
                      {getMessage('option.vehicleMileage.004')}
                    </option>
                    <option value="Upto 25000km" key="Upto 25000km">
                      {getMessage('option.vehicleMileage.005')}
                    </option>
                    <option value="Upto 30000km" key="Upto 30000km">
                      {getMessage('option.vehicleMileage.006')}
                    </option>
                    <option value="Upto 40000km" key="Upto 40000km">
                      {getMessage('option.vehicleMileage.007')}
                    </option>
                    <option value="Over 40000km" key="Over 40000km">
                      {getMessage('option.vehicleMileage.008')}
                    </option>
                    <option value="Unknown" key="Unknown">
                      {getMessage('option.vehicleMileage.009')}
                    </option>
                  </Select>

                  <ValidationError
                    id="vehicleMileageError"
                    requireVisible={false}
                    errorId={buildElementId(insurance.id, 'VEHICLE_MILEAGE')}
                    errorMessageId="error.fill.in.missing.information"
                  />
                </FormRow>
              )}
            </Form>
          </div>
        )}
    </div>
  );
};

const showElectricBicycleAlert = (noElectricMotorAssistanceOver25Kph, motorPowerOver250W, weighMoreThan25Kg) => {
    return noElectricMotorAssistanceOver25Kph === "Yes" && motorPowerOver250W === "No"  && weighMoreThan25Kg === "No"
}

const showLightElectricVehicleAlert = (topSpeedOver25Kph, weighMoreThan25Kg) => {
    return topSpeedOver25Kph === "No" && weighMoreThan25Kg === "No"
}

export const showElectricBicycleExtraQuestions = (noElectricMotorAssistanceOver25Kph, motorPowerOver250W, weighMoreThan25Kg) => {
  const doNotShow = noElectricMotorAssistanceOver25Kph === "Yes" && motorPowerOver250W === "No"  && weighMoreThan25Kg === "No"
  const missingValues = (noElectricMotorAssistanceOver25Kph === "" || motorPowerOver250W === "" || weighMoreThan25Kg === "") 
  return !doNotShow && !missingValues
}

export const showLightElectricVehicleExtraQuestions = (topSpeedOver25Kph, weighMoreThan25Kg) => {
  const doNotShow  = topSpeedOver25Kph === "No" && weighMoreThan25Kg === "No"
  const missingValues = (topSpeedOver25Kph === ""|| weighMoreThan25Kg === "")
  return !doNotShow && !missingValues
}

const showExtraQuestions = (vehicleDetails) => {
  if (vehicleDetails.electricPersonalTransportationDeviceVehicleType.value === 'Electric bicycle') {
    return showElectricBicycleExtraQuestions(
      vehicleDetails.noElectricMotorAssistanceOver25Kph?.value || "",
      vehicleDetails.motorPowerOver250W?.value || "",
      vehicleDetails.weighMoreThan25Kg?.value || ""
    )
  } else if (vehicleDetails.electricPersonalTransportationDeviceVehicleType.value === 'Light electric vehicle') {
    return showLightElectricVehicleExtraQuestions(
      vehicleDetails.topSpeedOver25Kph?.value || "",
      vehicleDetails.weighMoreThan25Kg?.value || ""
    )
  }
  else {
    return false
  }
  
}

const getWeighMoreLabel = (value) => {
  return value === 'Light electric vehicle' ? "scooterWeighMoreThan25Kg" : "weighMoreThan25Kg"
}

export default VehiclesQuestions;
